import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatAUD } from "@/context/CartContext";

const QuickViewModal = ({ product, onClose }) => {
  const { add, setOpen } = useCart();
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    setQty(1);
    setColor(product && product.colors && product.colors.length > 0 ? product.colors[0].name : null);
    setImgIndex(0);
  }, [product]);

  if (!product) return null;

  const activeColor = (product.colors || []).find((c) => c.name === color);
  const colorImage = activeColor && activeColor.image;
  const gallery = [colorImage || product.image, ...(product.gallery || [])]
    .filter((v, i, a) => v && a.indexOf(v) === i)
    .slice(0, 4);
  const displayImage = gallery[Math.min(imgIndex, gallery.length - 1)] || product.image;

  const addToCart = () => {
    add(product, qty, color);
    onClose();
    toast.success(`${product.name}${color ? ` (${color})` : ""} added to cart`, {
      action: { label: "View Cart", onClick: () => setOpen(true) },
    });
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="quick-view-modal"
        className="max-w-3xl p-0 overflow-hidden bg-marble border-gold/30 rounded-sm"
      >
        <div className="grid md:grid-cols-2">
          <div className="bg-white flex flex-col">
            <div className="relative overflow-hidden aspect-square">
              <img
                key={displayImage}
                data-testid="quick-view-image"
                src={displayImage}
                alt={product.name}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
            </div>
            {gallery.length > 1 && (
              <div data-testid="quick-view-thumbs" className="grid grid-cols-4 gap-2 p-3 bg-white border-t border-taupe/20">
                {gallery.map((img, i) => (
                  <button
                    key={img}
                    data-testid={`quick-view-thumb-${i}`}
                    onClick={() => setImgIndex(i)}
                    aria-label={`View photo ${i + 1}`}
                    className={`aspect-square overflow-hidden rounded-sm border-2 transition-all duration-200 ${
                      i === imgIndex ? "border-gold" : "border-taupe/30 hover:border-gold/50"
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-8 lg:p-10 flex flex-col">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{product.category}</p>
            <h2 className="font-serif-lux text-3xl lg:text-4xl text-charcoal leading-tight">{product.name}</h2>
            <div className="flex items-baseline gap-3 mt-4">
              <span data-testid="quick-view-price" className="text-xl font-medium text-charcoal">
                {formatAUD(product.price)}
              </span>
              {product.compare_at && (
                <span className="text-sm text-charcoal/40 line-through">{formatAUD(product.compare_at)}</span>
              )}
            </div>
            <p className="text-sm text-charcoal/60 font-light leading-relaxed mt-5">{product.description}</p>

            <ul className="mt-5 space-y-2">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm text-charcoal/70">
                  <Check size={14} className="text-gold flex-shrink-0" strokeWidth={2.5} />
                  {b}
                </li>
              ))}
            </ul>

            {product.colors && product.colors.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] tracking-[0.25em] uppercase text-charcoal/50 mb-3">
                  Colour — <span data-testid="quick-view-color-name" className="text-charcoal font-medium">{color}</span>
                </p>
                <div data-testid="quick-view-colors" className="flex items-center gap-3">
                  {product.colors.map((c) => (
                    <button
                      key={c.name}
                      data-testid={`color-option-${c.name.toLowerCase()}`}
                      onClick={() => setColor(c.name)}
                      aria-label={`Select colour ${c.name}`}
                      title={c.name}
                      style={{ backgroundColor: c.hex }}
                      className={`w-9 h-9 rounded-full border-2 transition-all duration-200 ${
                        color === c.name
                          ? "border-gold scale-110 shadow-[0_0_0_3px_rgba(212,175,55,0.25)]"
                          : "border-taupe/40 hover:border-gold/60"
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-taupe/30">
              <div className="flex items-center border border-taupe/50 rounded-full">
                <button
                  data-testid="quick-view-qty-minus"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center text-charcoal/60 hover:text-charcoal transition-colors"
                >
                  <Minus size={14} />
                </button>
                <span data-testid="quick-view-qty" className="w-8 text-center text-sm font-medium">{qty}</span>
                <button
                  data-testid="quick-view-qty-plus"
                  onClick={() => setQty((q) => Math.min(10, q + 1))}
                  className="w-10 h-10 flex items-center justify-center text-charcoal/60 hover:text-charcoal transition-colors"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                data-testid="quick-view-add-to-cart"
                onClick={addToCart}
                className="flex-1 py-3.5 bg-gold text-charcoal text-xs tracking-[0.25em] uppercase font-semibold rounded-full hover:bg-gold-dark transition-colors duration-300 active:scale-95"
              >
                Add to Cart — {formatAUD(product.price * qty)}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewModal;
